import {
  CLEO_IDENTITY_ACCENT,
  createMetalForgeTheme,
  resolveMetalForgeOrbInks,
  type MetalForgeOrbInks,
} from "@neucleos/theme/metalforge";
import type {
  ClientSettings,
  WorkingIndicatorKind,
  WorkingIndicatorLoader,
  WorkingIndicatorOrbLoop,
} from "@cleo/contracts";
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { useClientSettings } from "~/hooks/useSettings";
import { cn } from "~/lib/utils";
import {
  createThemeColorReader,
  metalforgeCanMove,
  metalforgeMotionAllowed,
} from "~/lib/metalforge";

// The generated pill (55 KB, WebGPU) loads only when a wait is actually shown;
// the generated loaders (CSS animation, no GPU) only when one is chosen.
const ThinkingOrbsPill = lazy(
  () => import("@neucleos/metalforge-web/effects/thinking-orbs/ThinkingOrbsPill.tsx"),
);
const Loader = lazy(() =>
  import("@neucleos/metalforge-web/effects/loaders/Loader.tsx").then((module) => ({
    default: module.Loader,
  })),
);

/**
 * The 38 loops in the generated file's ORB_STYLES order. The contract test
 * pins this order to the vendored bytes, so a re-capture that reorders the
 * loops goes red here instead of silently showing the wrong one.
 */
export const THINKING_ORB_LOOPS = [
  "breathe",
  "rings",
  "twinkle",
  "vortex",
  "band",
  "popcorn",
  "converge",
  "nest",
  "sag",
  "terminator",
  "stripes",
  "pinch",
  "rock",
  "spinup",
  "spot",
  "noise",
  "nested",
  "burst",
  "wave",
  "coil",
  "grid",
  "gyro",
  "drain",
  "tide",
  "cube",
  "ratchet",
  "funnel",
  "magnet",
  "layers",
  "ribbon",
  "rain",
  "ping",
  "arms",
  "crystal",
  "wedge",
  "bounce",
  "gimbal",
  "chase",
] as const;

export type ThinkingOrbLoop = (typeof THINKING_ORB_LOOPS)[number];

export interface WorkingIndicatorChoice {
  readonly kind: WorkingIndicatorKind;
  readonly loop: WorkingIndicatorOrbLoop;
  readonly loader: WorkingIndicatorLoader;
}

export type WorkingIndicatorSettings = Pick<
  ClientSettings,
  "workingIndicator" | "workingIndicatorOrbLoop" | "workingIndicatorLoader"
>;

/**
 * ST10: the person's choice wins. A wait may suggest a loop (`hint`) — the
 * media creator suggests Burst — and the suggestion shows only while the
 * person has left the loop at its default; an explicit choice of loop
 * outranks every suggestion. `override` is for previews and stories.
 */
export function resolveWorkingIndicator(
  settings: WorkingIndicatorSettings,
  options: {
    readonly hint?: WorkingIndicatorOrbLoop | undefined;
    readonly override?: Partial<WorkingIndicatorChoice> | undefined;
  } = {},
): WorkingIndicatorChoice {
  const chosenLoop = settings.workingIndicatorOrbLoop;
  return {
    kind: options.override?.kind ?? settings.workingIndicator,
    loop:
      options.override?.loop ??
      (chosenLoop === "breathe" && options.hint !== undefined ? options.hint : chosenLoop),
    loader: options.override?.loader ?? settings.workingIndicatorLoader,
  };
}

export interface ThinkingOrbProps {
  /** What is being waited for — the accessible name; the indicator carries no text. */
  readonly label: string;
  /** The loop this wait suggests; shown only while the person has not chosen a loop (ST10). */
  readonly loop?: ThinkingOrbLoop;
  /** Ball size in CSS px. */
  readonly size?: number;
  /** The accent the indicator wears; Cleo's identity by default. */
  readonly accent?: string;
  /**
   * The still signal shown whenever the indicator cannot or must not move: the
   * quiet-dots choice, no WebGPU for the orb, reduced motion, ambient motion
   * off, a stylesheet-less host, or the generated chunk still loading.
   */
  readonly fallback: ReactNode;
  readonly className?: string;
  /** An explicit choice, for the Appearance preview and stories; otherwise the person's settings. */
  readonly indicator?: Partial<WorkingIndicatorChoice>;
}

/**
 * The working indicator at the app boundary (ST10): the person's choice of
 * orb, loader or quiet dots, mounted only while the named wait is alive,
 * wearing the theme's inks, with its still fallback carrying the meaning
 * whenever motion is unavailable. WebGPU gates only the orb; reduced motion
 * and the ambient-motion switch still every kind.
 */
export function ThinkingOrb({
  label,
  loop,
  size = 22,
  accent = CLEO_IDENTITY_ACCENT,
  fallback,
  className,
  indicator,
}: ThinkingOrbProps) {
  const hostRef = useRef<HTMLSpanElement>(null);
  const [inks, setInks] = useState<MetalForgeOrbInks | null>(null);
  const workingIndicator = useClientSettings((settings) => settings.workingIndicator);
  const workingIndicatorOrbLoop = useClientSettings((settings) => settings.workingIndicatorOrbLoop);
  const workingIndicatorLoader = useClientSettings((settings) => settings.workingIndicatorLoader);
  const choice = resolveWorkingIndicator(
    { workingIndicator, workingIndicatorOrbLoop, workingIndicatorLoader },
    { hint: loop, override: indicator },
  );
  const kind = choice.kind;

  useEffect(() => {
    if (kind === "dots") {
      setInks(null);
      return;
    }
    const allowed = kind === "orb" ? metalforgeCanMove() : metalforgeMotionAllowed();
    if (!allowed) {
      setInks(null);
      return;
    }
    setInks(
      resolveMetalForgeOrbInks(
        createMetalForgeTheme({ accent }),
        createThemeColorReader(hostRef.current),
      ),
    );
  }, [accent, kind]);

  const live = kind !== "dots" && inks !== null;
  const loopIndex = Math.max(0, THINKING_ORB_LOOPS.indexOf(choice.loop));
  return (
    <span
      ref={hostRef}
      role="status"
      aria-label={label}
      data-thinking-orb={!live ? "still" : kind === "orb" ? choice.loop : `loader:${choice.loader}`}
      data-working-indicator={kind}
      className={cn("inline-flex shrink-0 items-center", className)}
    >
      {!live || inks === null ? (
        fallback
      ) : kind === "orb" ? (
        <Suspense fallback={fallback}>
          <ThinkingOrbsPill
            loop={loopIndex}
            ballSize={size}
            label={label}
            showsPill={false}
            showsLabel={false}
            accent={inks.accent}
            dotColor={inks.dot}
            dotColorLight={inks.dot}
            pill={inks.pill}
            pillLight={inks.pill}
            labelColor={inks.label}
            labelColorLight={inks.label}
            containerStyle={{ width: size, height: size, minHeight: 0 }}
          />
        </Suspense>
      ) : (
        <Suspense fallback={fallback}>
          <Loader
            background={inks.pill}
            color={inks.accent}
            label={null}
            name={choice.loader}
            size={size}
            track={inks.dot}
          />
        </Suspense>
      )}
    </span>
  );
}
